import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CreditCard, Truck, User, Home, Phone, Mail, X, AlertCircle } from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";
import AuthenticationModal from "./AuthenticationModal";
import EmptyCartNotice from "./EmptyCartNotice";
import StatusMessage from "./StatusMessage";
import BillingForm from "./BillingForm";
import OrderSummary from "./OrderSummary";
import SignUp from "./SignUp";

// Constants
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_API = process.env.REACT_APP_AUTH0_API;

function AuthLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
      {children}
    </div>
  );
}

// Profile Completion Modal Component
function ProfileCompletionModal({ isOpen, onClose, onCompleteProfile }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={20} />
        </button>
        
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
            <AlertCircle className="h-6 w-6 text-yellow-600" />
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Complete Your Profile
          </h3>
          
          <p className="text-sm text-gray-500 mb-6">
            To proceed with checkout, please complete your profile information first. 
            This helps us process your order more efficiently.
          </p>
          
          <div className="flex gap-3 justify-center">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onCompleteProfile}
              className="px-4 py-2 text-sm font-medium text-white bg-[#D4AF37] border border-transparent rounded-md hover:bg-[#B8941F] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D4AF37] transition-colors"
            >
              Complete Profile
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BillingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { cartItems: initialCartItems = [] } = location.state || {};

  const { 
    user: auth0User, 
    isLoading: auth0Loading, 
    isAuthenticated, 
    loginWithRedirect, 
    getAccessTokenSilently 
  } = useAuth0();

  const [userProfile, setUserProfile] = useState(null);
  const [cartItems, setCartItems] = useState(initialCartItems);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);  
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    sameAsBilling: true,
    shippingAddress: "",
    shippingCity: "",
    shippingState: "",
    shippingZip: "",
    paymentMethod: "card",
  });

  // Fetch user profile data from API using Auth0 token
  const fetchUserProfile = async (email) => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH_API
        }
      });
      
      const encodedEmail = encodeURIComponent(email);
      const response = await fetch(`${API_BASE_URL}/get-user-profile/${encodedEmail}/`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      });
      
      if (response.ok) {
        const profileData = await response.json();
        setUserProfile(prev => ({ ...prev, ...profileData }));
        
        setFormData(prevData => ({
          ...prevData,
          fullName: profileData.name || `${profileData.first_name} ${profileData.last_name}`,
          email: profileData.email || email,
          phone: profileData.phone || "",
          address: profileData.address?.street || "",
          city: profileData.address?.city || "",
          state: profileData.address?.state || "",
          zip: profileData.address?.zip || "",
        }));
      } else if (response.status === 404) {
        const errorData = await response.json();
        if (errorData.error === "User not found") {
          console.log("User profile not found, showing completion modal");
          setShowProfileModal(true);
        }
      } else {
        console.error("Failed to fetch user profile:", await response.text());
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch cart items from GetCartView using Auth0 token
  const fetchCartItems = async (email) => {
    setCartLoading(true);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH_API
        }
      });
      
      console.log("Fetching cart for email:", email);
      console.log("Using token:", token ? "Token present" : "No token");
      
      const encodedEmail = encodeURIComponent(email);
      const url = `${API_BASE_URL}/cart/${encodedEmail}/`;
      console.log("Fetching from URL:", url);
      
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      });
      
      console.log("Cart fetch response status:", response.status);
      
      if (response.ok) {
        const cartData = await response.json();
        console.log("Cart data received:", cartData);
        
        // Ensure cartData is an array
        if (Array.isArray(cartData)) {
          setCartItems(cartData);
          console.log("Cart items set:", cartData.length, "items");
        } else if (cartData && cartData.cart_items) {
          // Handle if the response has a nested structure
          setCartItems(cartData.cart_items);
          console.log("Cart items set from nested structure:", cartData.cart_items.length, "items");
        } else {
          console.warn("Unexpected cart data structure:", cartData);
          setCartItems([]);
        }
      } else {
        const errorText = await response.text();
        console.error("Failed to fetch cart items. Status:", response.status, "Error:", errorText);
        setCartItems([]);
      }
    } catch (error) {
      console.error("Error fetching cart items:", error);
      setCartItems([]);
    } finally {
      setCartLoading(false);
    }
  };

  // Effect to check authentication status and fetch data
  useEffect(() => {
    if (!auth0Loading) {
      if (isAuthenticated && auth0User) {
        console.log("User authenticated:", auth0User.email);
        
        // Fetch profile
        fetchUserProfile(auth0User.email);
        
        // Only fetch cart if we don't have initial cart items
        if (!initialCartItems || initialCartItems.length === 0) {
          console.log("No initial cart items, fetching from API");
          fetchCartItems(auth0User.email);
        } else {
          console.log("Using initial cart items:", initialCartItems.length);
          setCartItems(initialCartItems);
        }
      } else {
        setLoading(false);
      }
    }
  }, [auth0Loading, isAuthenticated, auth0User]);

  // Helper function to scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  // Redirect to Auth0 login
  const handleRedirectToAuth = () => {
    loginWithRedirect({
      appState: { returnTo: location.pathname }
    });
  };

  // Handle profile completion modal actions
  const handleCompleteProfile = () => {
    sessionStorage.setItem('billingPageState', JSON.stringify({
      cartItems,
      formData,
      appliedDiscount,
      returnPath: location.pathname
    }));
    
    navigate('/profile');
  };

  const handleCloseProfileModal = () => {
    setShowProfileModal(false);
    navigate('/shop');
  };

  // Check if cart has items
  const hasCartItems = cartItems && cartItems.length > 0;
  
  // Calculate shipping cost based on state
  const calculateShippingCost = () => {
    const relevantState = formData.sameAsBilling ? formData.state : formData.shippingState;
    return relevantState && relevantState.toLowerCase() === "kerala" ? 60 : 120;
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.total_price, 0);
  const shippingCost = hasCartItems ? calculateShippingCost() : 0;
  const total = subtotal + shippingCost;
  const finalTotal = total - appliedDiscount;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    
    if (formError) {
      setFormError(null);
    }
  };

  const handleDiscountUpdate = (discountAmount) => {
    setAppliedDiscount(discountAmount);
  };

  const initiatePayment = async (orderData) => {
    try {
      setIsProcessingPayment(true);
      
      const currentDomain = window.location.origin;
      const orderParams = new URLSearchParams({
        orderId: orderData.order_id,
        total: finalTotal.toString(),
        status: 'success' 
      }).toString();
      
      const redirectUrl = `${currentDomain}/payment-callback?${orderParams}`;
      
      const paymentData = {
        amount: finalTotal,
        redirect_url: redirectUrl
      };

      const response = await fetch(`${API_BASE_URL}/api/phonepe/create-payment/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(paymentData),
      });

      if (response.ok) {
        const paymentResponse = await response.json();
        
        if (paymentResponse.success && paymentResponse.checkout_url) {
          sessionStorage.setItem('pendingOrder', JSON.stringify({
            orderData,
            billingInfo: formData,
            appliedDiscount,
            finalTotal,
            merchantOrderId: paymentResponse.merchant_order_id,
            paymentAmount: paymentResponse.amount
          }));
          
          window.location.href = paymentResponse.checkout_url;
        } else {
          throw new Error(paymentResponse.error || "Payment initiation failed - no checkout URL received");
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to initiate payment");
      }
    } catch (error) {
      console.error("Payment initiation error:", error);
      setFormError(`Payment initiation failed: ${error.message}`);
      setIsProcessingPayment(false);
      scrollToTop();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      handleRedirectToAuth();
      return;
    }
    
    if (showProfileModal || !userProfile) {
      setShowProfileModal(true);
      return;
    }
    
    if (!hasCartItems) {
      alert("Your cart is empty. Please add items before proceeding to checkout.");
      navigate("/shop");
      return;
    }
    
    setIsSubmitting(true);
    setFormError(null);
    
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH_API
        }
      });
      
      const orderData = {
        user_id: auth0User.email,
        cart_items: cartItems.map(item => ({
          cart_id: item.cart_id,
          product_id: item.product_id,
          name: item.name,
          size: item.size,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          image: item.image
        })),
        billing_info: {
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          paymentMethod: formData.paymentMethod
        },
        shipping_info: formData.sameAsBilling
          ? {
              address: formData.address,
              city: formData.city,
              state: formData.state,
              zip: formData.zip,
            }
          : {
              address: formData.shippingAddress,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingZip,
            },
        order_summary: {
          subtotal,
          shippingCost,
          discount: appliedDiscount,
          total,
          finalTotal,
        },
        status: "pending",
      };

      const response = await fetch(`${API_BASE_URL}/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(orderData),
      });

      const responseData = await response.json();
      
      if (response.ok) {
        setOrderSuccess(true);
        await initiatePayment(responseData);
      } else {
        if (response.status === 400) {
          setFormError(responseData.error || "Please check your form details and try again.");
        } else if (response.status === 404) {
          setFormError(responseData.error || "User or cart items not found.");
        } else {
          setFormError(responseData.error || "An error occurred while processing your order.");
        }
        setOrderSuccess(false);
        scrollToTop(); 
      }
    } catch (error) {
      console.error("Error submitting order:", error);
      setFormError("Network error. Please check your connection and try again.");
      setOrderSuccess(false);
      scrollToTop();
    } finally {
      setIsSubmitting(false);
    }
  };

  // If Auth0 is still loading, show loading state
  if (auth0Loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#333333]">Checking authentication...</p>
      </div>
    );
  }

  // If user is not authenticated, show SignUp component
  if (!isAuthenticated) {
    return (
      <AuthLayout>
        <SignUp redirectPath={location.pathname} />
      </AuthLayout>
    );
  }

  // If profile is still loading
  if (loading || cartLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#333333] mb-2">
            {loading ? "Loading Profile..." : "Loading Cart..."}
          </p>
          {cartLoading && (
            <p className="text-sm text-gray-500">Fetching your cart items...</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-1 px-4 md:px-8 mt-12">
      <div className="max-w-6xl mx-auto">
        <h1
          className="text-3xl md:text-4xl text-[#333333] text-center mb-8 relative"
          style={{ fontFamily: "serif" }}
        >
          Billing & Shipping
          <span className="block w-24 h-1 bg-[#D4AF37] mx-auto mt-2"></span>
        </h1>

        {/* Debug info - remove in production */}
        {/* {process.env.NODE_ENV === 'development' && (
          <div className="mb-4 p-4 bg-blue-50 rounded text-sm">
            <p>Cart Items: {cartItems.length}</p>
            <p>Has Cart Items: {hasCartItems ? 'Yes' : 'No'}</p>
            <p>User Email: {auth0User?.email}</p>
          </div>
        )} */}

        <EmptyCartNotice hasCartItems={hasCartItems} navigate={navigate} />
        <StatusMessage formError={formError} />

        <div className="flex flex-col lg:flex-row gap-8">
          <BillingForm 
            formData={formData}
            handleInputChange={handleInputChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting || isProcessingPayment}
            hasCartItems={hasCartItems}
            finalTotal={finalTotal}
            isProcessingPayment={isProcessingPayment}
          />

          <OrderSummary 
            cartItems={cartItems}
            hasCartItems={hasCartItems}
            subtotal={subtotal}
            shippingCost={shippingCost}
            total={total}
            onDiscountUpdate={handleDiscountUpdate}
          />
        </div>
      </div>

      <ProfileCompletionModal
        isOpen={showProfileModal}
        onClose={handleCloseProfileModal}
        onCompleteProfile={handleCompleteProfile}
      />
    </div>
  );
}